import * as swcHelpers from '@swc/helpers';
import { constrain, Constraint, adjust, Type, optional, either } from 'specified';
import { integerSpec, positiveIntegerSpec, isoDateSpec, dateStringSpec } from '../specs-utils.js';

const integerAbove0 = constrain(integerSpec, [
    Constraint.number.above(0)
]);
const localizedStringSpec = adjust(Type.map(Type.string, Type.string), {
    skipInvalidValues: true
});
const addressSpec = adjust(Type.map(Type.string, Type.string), {
    skipInvalidValues: true
});
const money = Type.interface({
    currency: Type.string,
    amount: positiveIntegerSpec,
    precision: integerAbove0
});
const mediaImages = Type.interface({
    url: Type.string,
    dimensions: Type.interface({
        w: Type.number,
        h: Type.number
    }),
    label: optional(Type.string)
});
const productSchemaBase = {
    name: localizedStringSpec,
    sku: Type.string,
    imageId: optional(Type.string),
    media: optional(Type.interface({
        images: Type.array(mediaImages)
    }))
};
const attributesSchemaBase = {
    brand: Type.string,
    brandModel: optional(Type.string),
    brandModelCode: optional(Type.string),
    opcoBackendId: optional(Type.string),
    supplierEAN: optional(Type.string)
};
const contactLensAttributeTypes = either(Type.literal({
    'Contact-lenses': 1
}), Type.literal({
    CONTACT_LENSES: 1
}));
const contactLensBundleAttributeTypes = either(Type.literal({
    'Contact-lens-bundles': 1
}), Type.literal({
    CONTACT_LENS_BUNDLES: 1
}));
const contactLensesAttributesSchemaBase = {
    packageQuantity: positiveIntegerSpec,
    replacementFrequency: localizedStringSpec,
    eyeSide: optional(Type.literal({
        left: 1,
        right: 1,
        both: 1
    })),
    isSubscription: optional(Type.boolean),
    // temporary property, needs be removed when LiquidPixel is replaced with Bynder
    sapArticleNumber: optional(Type.string)
};
const contactLensesProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: contactLensAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(contactLensesAttributesSchemaBase)
}));
const contactLensesBundleAttributesSchemaBase = {
    lensType: localizedStringSpec,
    isSubscription: Type.boolean,
    months: positiveIntegerSpec,
    imageSku: optional(Type.string),
    fixedDiscount: optional(Type.number),
    leftLensAxis: optional(Type.number),
    leftLensBaseCurve: optional(Type.number),
    leftLensCylinder: optional(Type.number),
    leftLensDiameter: optional(Type.number),
    leftLensName: optional(Type.string),
    leftLensPower: optional(Type.number),
    leftLensSku: optional(Type.string),
    leftLensVariantSku: optional(Type.string),
    rightLensAxis: optional(Type.number),
    rightLensBaseCurve: optional(Type.number),
    rightLensCylinder: optional(Type.number),
    rightLensDiameter: optional(Type.number),
    rightLensName: optional(Type.string),
    rightLensPower: optional(Type.number),
    rightLensSku: optional(Type.string),
    rightLensVariantSku: optional(Type.string),
    solutionName: optional(Type.string),
    solutionSku: optional(Type.string),
    bundleProduct1Media: optional(Type.interface({
        images: Type.array(mediaImages)
    })),
    bundleProduct2Media: optional(Type.interface({
        images: Type.array(mediaImages)
    })),
    bundleProduct3Media: optional(Type.interface({
        images: Type.array(mediaImages)
    }))
};
const contactLensesBundleSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: contactLensBundleAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(contactLensesBundleAttributesSchemaBase)
}));
/*
  The baseCurve, diameter and power parameters are temporarily made optional due to the issue
  with order-service. Needs to be reverted later
  See the issue: https://jira.grandvision.global/browse/MACC-3839
*/ const contactLensesProductDetailsSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: contactLensAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, contactLensesAttributesSchemaBase, {
        baseCurve: optional(Type.string),
        diameter: optional(Type.string),
        power: optional(Type.string),
        axis: optional(Type.string),
        cylinder: optional(Type.string),
        addition: optional(Type.string)
    }))
}));
const solutionAttributeTypes = either(Type.literal({
    Solutions: 1
}), Type.literal({
    SOLUTIONS: 1
}));
const solutionProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: solutionAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface({
        brand: attributesSchemaBase.brand,
        solutionType: optional(Type.string),
        volume: positiveIntegerSpec,
        opcoBackendId: optional(Type.string)
    })
}));
const sunglassesAttributeTypes = either(Type.literal({
    sunglasses: 1
}), Type.literal({
    SUNGLASSES: 1
}));
const sunglassesSchema = swcHelpers.objectSpread({
    attributesType: sunglassesAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, attributesSchemaBase))
});
const sunglassesProductSpec = Type.interface(sunglassesSchema);
const prescriptionGlassesAttributeTypes = either(Type.literal({
    prescriptionGlasses: 1
}), Type.literal({
    PRESCRIPTION_GLASSES: 1
}));
const Prescription = adjust(Type.map(Type.string, Type.number), {
    skipInvalidValues: true
});
const PrescriptionGlassesMoney = Type.interface({
    currencyCode: Type.string,
    centAmount: Type.number,
    fractionDigits: Type.number,
    type: Type.string
});
const readyReadersAttributeTypes = either(Type.literal({
    readyReaders: 1
}), Type.literal({
    READY_READERS: 1
}));
const readyReadersSchema = swcHelpers.objectSpread({
    attributesType: readyReadersAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, attributesSchemaBase))
});
const readyReadersProductSpec = Type.interface(readyReadersSchema);
const BundlePrice = Type.interface({
    id: Type.string,
    value: PrescriptionGlassesMoney,
    discounted: optional(Type.interface({
        value: PrescriptionGlassesMoney
    }))
});
const variant = Type.interface({
    sku: Type.string,
    media: optional(Type.interface({
        images: Type.array(mediaImages)
    }))
});
const prescriptionMetaDataSchema = {
    expiryDate: optional(isoDateSpec),
    opticianDetails: optional(Type.string)
};
const prescriptionDataSchema = swcHelpers.objectSpread({}, prescriptionMetaDataSchema, {
    rightLens: optional(Prescription),
    leftLens: optional(Prescription)
});
Type.interface(prescriptionDataSchema);
const existingCustomer = Type.interface({
    type: Type.literal({
        ExistingCustomer: 1
    })
});
const newPrescription = Type.interface(swcHelpers.objectSpread({
    type: Type.literal({
        NewPrescription: 1
    })
}, prescriptionDataSchema));
const prescriptionFile = Type.interface({
    type: Type.literal({
        PrescriptionFile: 1
    }),
    fileName: Type.string,
    storageId: Type.string,
    pupillaryDistanceFileFileName: Type.string,
    pupillaryDistanceFileStorageId: Type.string
});
const emailPrescription = Type.interface({
    type: Type.literal({
        EmailPrescription: 1
    })
});
const bundleChildLineItemBase = {
    productId: Type.string,
    productType: Type.string,
    name: localizedStringSpec,
    price: BundlePrice
};
const bundleMainLineItemBase = {
    price: BundlePrice,
    productType: Type.string,
    opcoBackendProductId: optional(Type.string)
};
const bundleChildLineItem = Type.interface(swcHelpers.objectSpread({}, bundleChildLineItemBase, {
    variant
}));
const bundleMainLineItem = Type.interface(swcHelpers.objectSpread({}, bundleMainLineItemBase, {
    variant
}));
const prescriptionGlassesProductSpec = Type.interface(swcHelpers.objectSpread({}, sunglassesSchema, {
    attributesType: prescriptionGlassesAttributeTypes,
    lineItemDetail: Type.interface({
        bundleMainLineItem,
        bundleChildLineItems: Type.array(bundleChildLineItem),
        prescription: optional(either(existingCustomer, newPrescription, prescriptionFile, emailPrescription))
    })
}));
const nonPrescriptionBundlesTypes = either(Type.literal({
    nonPrescriptionBundle: 1
}), Type.literal({
    NON_PRESCRIPTION_BUNDLE: 1
}));
const nonPrescriptionBundlesSchema = swcHelpers.objectSpread({
    attributesType: nonPrescriptionBundlesTypes
}, productSchemaBase, {
    attributes: Type.interface({
        includesLenses: Type.boolean
    }),
    lineItemDetail: Type.interface({
        bundleMainLineItem: Type.interface(bundleMainLineItemBase),
        bundleChildLineItems: Type.array(Type.interface(bundleChildLineItemBase)),
        configurationBundle: Type.interface(swcHelpers.objectSpread({}, bundleChildLineItemBase, {
            isPrescriptionNeeded: Type.boolean,
            includesLenses: Type.boolean
        }))
    })
});
const nonPrescriptionBundleProductSpec = Type.interface(nonPrescriptionBundlesSchema);
const framesAttributeTypes = either(Type.literal({
    frames: 1
}), Type.literal({
    FRAMES: 1
}));
const framesProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: framesAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, attributesSchemaBase))
}));
const clsPriceSpec = Type.interface({
    discount: Type.interface({
        amount: Type.number,
        percentage: Type.number
    }),
    net: Type.number,
    subtotal: Type.number,
    tax: Type.interface({
        amount: Type.number,
        portions: Type.array(Type.interface({
            rate: Type.number,
            amount: Type.number
        }))
    }),
    gross: Type.number
});
const contactLensSubscriptionItem = Type.interface({
    gan: Type.string,
    mediaImages: Type.array(mediaImages),
    name: Type.string,
    packageQuantity: optional(Type.number),
    quantity: Type.number,
    ratePlanId: Type.string,
    regularImages: Type.array(mediaImages),
    sku: Type.string,
    type: Type.literal({
        SOLUTION: 1,
        LENS: 1
    }),
    price: Type.interface({
        total: clsPriceSpec,
        unit: clsPriceSpec
    })
});
const lensPrescription = Type.interface({
    gan: Type.string,
    sku: Type.string,
    baseCurve: Type.number,
    diameter: Type.number,
    power: Type.number,
    axis: optional(Type.number),
    cylinder: optional(Type.number),
    addition: optional(Type.string)
});
const contactLensSubscriptionAttributeTypes = either(Type.literal({
    contactLensSubscription: 1
}), Type.literal({
    CONTACT_LENS_SUBSCRIPTION: 1
}));
const contactLensSubscriptionProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: contactLensSubscriptionAttributeTypes
}, productSchemaBase, {
    lineItemDetail: Type.interface({
        bundleLineItems: Type.array(contactLensSubscriptionItem)
    }),
    typeDetails: Type.interface({
        billingFrequency: Type.literal({
            MONTHLY: 1,
            QUARTERLY: 1
        }),
        deliveryFrequency: Type.literal({
            MONTHLY: 1,
            QUARTERLY: 1
        }),
        subscriptionStartDate: Type.string,
        prescription: Type.interface({
            leftLens: optional(lensPrescription),
            rightLens: optional(lensPrescription)
        })
    })
}));
const accessoriesAttributeTypes = either(Type.literal({
    accessories: 1
}), Type.literal({
    ACCESSORIES: 1
}));
const accessoriesSchema = swcHelpers.objectSpread({
    attributesType: accessoriesAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, attributesSchemaBase))
});
const accessoriesProductSpec = Type.interface(accessoriesSchema);
const otherProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: optional(Type.literal({}))
}, productSchemaBase, {
    attributes: Type.interface({})
}));
swcHelpers.objectSpread({}, accessoriesAttributeTypes, contactLensAttributeTypes, contactLensSubscriptionAttributeTypes, solutionAttributeTypes, sunglassesAttributeTypes, framesAttributeTypes, contactLensBundleAttributeTypes, prescriptionGlassesAttributeTypes, readyReadersAttributeTypes, nonPrescriptionBundlesTypes);
const lineItemSchemaBase = {
    catalogPrice: money,
    discountedPrice: optional(money),
    totalPrice: optional(money),
    includedDiscountDescription: optional(localizedStringSpec),
    quantity: integerAbove0
};
Type.interface(swcHelpers.objectSpread({
    product: nonPrescriptionBundleProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: prescriptionGlassesProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: contactLensesProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: contactLensesBundleSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: solutionProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: framesProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: sunglassesProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: contactLensSubscriptionProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: otherProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: readyReadersProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: accessoriesProductSpec
}, lineItemSchemaBase));
const historyLineItem = Type.interface(swcHelpers.objectSpread({
    product: either(accessoriesProductSpec, contactLensesProductSpec, contactLensesBundleSpec, solutionProductSpec, framesProductSpec, sunglassesProductSpec, nonPrescriptionBundleProductSpec, prescriptionGlassesProductSpec, contactLensSubscriptionProductSpec, otherProductSpec, readyReadersProductSpec)
}, lineItemSchemaBase));
const detailsLineItem = Type.interface(swcHelpers.objectSpread({
    product: either(accessoriesProductSpec, contactLensesProductDetailsSpec, contactLensesBundleSpec, solutionProductSpec, framesProductSpec, sunglassesProductSpec, nonPrescriptionBundleProductSpec, prescriptionGlassesProductSpec, contactLensSubscriptionProductSpec, otherProductSpec, readyReadersProductSpec)
}, lineItemSchemaBase));
const orderBaseSchema = {
    provider: Type.string,
    orderNumber: constrain(Type.string, [
        Constraint.string.regex(/^[0-9]+$/)
    ]),
    orderDate: dateStringSpec,
    shipmentState: optional(Type.literal({
        Shipped: 1,
        Ready: 1,
        Pending: 1,
        Delayed: 1,
        Partial: 1,
        Backorder: 1
    })),
    paymentMethod: optional(Type.literal({
        bancontactCard: 1,
        bancontactMobile: 1,
        cashOnDelivery: 1,
        creditcard: 1,
        fake: 1,
        ideal: 1,
        klarna_account: 1,
        klarna: 1,
        mbway: 1,
        mobilepay: 1,
        multibanco: 1,
        paypal: 1,
        sepadirectdebit: 1,
        oney: 1,
        swish: 1
    })),
    paymentState: optional(Type.literal({
        BalanceDue: 1,
        CreditOwed: 1,
        Failed: 1,
        Paid: 1,
        Pending: 1
    })),
    totalChargedPrice: money,
    trackingURL: optional(Type.string)
};
const orderHistoryItemSpec = Type.interface(swcHelpers.objectSpread({}, orderBaseSchema, {
    lineItems: Type.array(historyLineItem)
}));
const orderHistoryResponseSpec = Type.interface({
    orders: Type.array(orderHistoryItemSpec),
    total: positiveIntegerSpec
});
const orderDetailsItemSpec = Type.interface(swcHelpers.objectSpread({}, orderBaseSchema, {
    lineItems: Type.array(detailsLineItem),
    totalProductsPrice: money,
    shippingPrice: money,
    totalAdditionalDiscount: money,
    shippingAddress: addressSpec,
    billingAddress: addressSpec,
    shippingInfo: Type.interface({
        deliveryMethod: Type.string
    }),
    customerInfo: Type.interface({
        email: Type.string,
        phone: optional(Type.string)
    })
}));
const orderDetailsResponseSpec = Type.interface({
    order: orderDetailsItemSpec
});

export { Prescription, accessoriesProductSpec, bundleChildLineItem, bundleMainLineItem, contactLensAttributeTypes, contactLensSubscriptionProductSpec, contactLensesBundleSpec, contactLensesProductDetailsSpec, contactLensesProductSpec, detailsLineItem, framesProductSpec, historyLineItem, lensPrescription, mediaImages, money, nonPrescriptionBundleProductSpec, orderDetailsItemSpec, orderDetailsResponseSpec, orderHistoryItemSpec, orderHistoryResponseSpec, otherProductSpec, prescriptionDataSchema, prescriptionGlassesProductSpec, readyReadersProductSpec, solutionProductSpec, sunglassesProductSpec };
//# sourceMappingURL=specs.js.map
